import { FC, useCallback, useState } from "react";
import { BottomSheet, Radio, Search } from "@praticabr/ppsa";
import { useIntl } from "react-intl";
import translate from "../../i18n/translate";
import { DivSelectLangStyle, LanguageOptions } from "./styles";
import { db } from "../../../db/db";
import { useLiveQuery } from "dexie-react-hooks";
import { updateConfigUser } from "../../../services";
import { useOutsideClick } from "../../../hooks/use-outside-click";
import { useScreen } from "../../../context/ScreenContext";

type SelectLangProps = {
  isLogin: boolean;
};

const languageOptions = [
  { value: "PT", label: "Português" },
  { value: "CS", label: "Čeština" },
  { value: "DA", label: "Dansk" },
  { value: "DE", label: "Deutsch" },
  { value: "EN", label: "English" },
  { value: "ES", label: "Español" },
  { value: "FR", label: "Français" },
  { value: "IT", label: "Italiano" },
  { value: "HU", label: "Magyar" },
  { value: "NO", label: "Norsk" },
  { value: "PL", label: "Polskie" },
  { value: "FI", label: "Suomi" },
  { value: "SV", label: "Svenska" },
  { value: "RU", label: "Русский" },
  { value: "EL", label: "Ελληνικά" },
  { value: "ZH", label: "中文" },
];

const SelectLang: FC<SelectLangProps> = (props) => {
  const intl = useIntl();
  const languagePickerId = props.isLogin ? "login-language" : "user-language";
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchKey, setSearchKey] = useState(0);
  const [listOptions, setListOptions] = useState(languageOptions);
  const { isMobile } = useScreen();
  const wrapperRef = useOutsideClick(() => {
    if (!isMobile) setShowDropdown(false);
  });
  const userDb = useLiveQuery(() => db.user.toArray());
  const userConfigsDb = useLiveQuery(() => db.userConfigs.toArray());
  const id =
    userConfigsDb !== undefined && userConfigsDb.length !== 0
      ? userConfigsDb[0].id
        ? userConfigsDb[0].id
        : 0
      : 0;
  const lang =
    userConfigsDb !== undefined && userConfigsDb.length !== 0
      ? userConfigsDb[0].language
        ? userConfigsDb[0].language
        : "PT"
      : "PT";

  const chooseLanguageHandler = (value: string) => {
    const submit = {
      userConfigs: {
        id: userConfigsDb && userConfigsDb[0].id,
        userId: userConfigsDb && userConfigsDb[0].userId,
        language: value,
      },
    };

    props.isLogin
      ? db.userConfigs.update(id, { language: value })
      : updateConfigUser(submit, userDb ? userDb[0].accessToken : "")
          .then((resp) => {
            db.userConfigs.update(id, submit.userConfigs);
          })
          .catch((error) => {
            console.error(error);
          });
    setShowDropdown(false);
  };

  const onCloseLanguageModal = useCallback(() => {
    setShowDropdown(false);
  }, []);

  return (
    <>
      <DivSelectLangStyle
        id="joyride-language"
        isLogin={props.isLogin}
        ref={wrapperRef}
      >
        <p
          className="selected"
          onClick={() => {
            if (!showDropdown) setSearchKey((currentKey) => currentKey + 1);
            setListOptions(languageOptions);
            setShowDropdown(!showDropdown);
          }}
        >
          <span>{translate("main.Idioma")}:</span>
          <span className="language">{lang}</span>
          <span className={showDropdown ? "up-arrow" : "down-arrow"}></span>
        </p>
        {!isMobile && showDropdown && (
          <ul className="dropdown">
            {languageOptions.map((option) => (
              <li
                key={option.value}
                onClick={() => chooseLanguageHandler(option.value)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </DivSelectLangStyle>
      {isMobile && (
        <BottomSheet.root
          show={showDropdown}
          onClose={onCloseLanguageModal}
          draggable={false}
          title={intl.formatMessage({ id: "main.Idioma" })}
        >
          <BottomSheet.body className="ppsa-bottom-sheet__body ppsa-bottom-sheet__body--options">
            {languageOptions.length > 10 && (
              <div className="ppsa-bottom-sheet__search">
                <Search
                  key={searchKey}
                  placeholder={intl.formatMessage({ id: "main.Procurar" })}
                  onChangeValues={(text) => {
                    const search = (text ?? "").toLowerCase();
                    setListOptions(
                      languageOptions.filter(
                        (option) =>
                          option.label.toLowerCase().includes(search) ||
                          option.value.toLowerCase().includes(search),
                      ),
                    );
                  }}
                />
              </div>
            )}
            <LanguageOptions className="ppsa-select-component ppsa-select-bottom-sheet">
              <div
                className="ppsa-content-options-container"
                role="radiogroup"
                aria-label={intl.formatMessage({ id: "main.Idioma" })}
              >
                {listOptions.map((option) => (
                  <div className="ppsa-content-option" key={option.value}>
                    <label
                      className="ppsa-content-item-option"
                      htmlFor={`${languagePickerId}-${option.value}-mobile`}
                    >
                      <Radio
                        id={`${languagePickerId}-${option.value}-mobile`}
                        name={`${languagePickerId}-mobile-options`}
                        value={option.value}
                        variant="standard"
                        checked={option.value === lang}
                        onChange={() => chooseLanguageHandler(option.value)}
                      />
                      <div className="ppsa-option">{option.label}</div>
                    </label>
                  </div>
                ))}
              </div>
            </LanguageOptions>
          </BottomSheet.body>
        </BottomSheet.root>
      )}
    </>
  );
};

export default SelectLang;
